import { db } from "@/firebaseConfig";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";

// Records anonymized member-session telemetry into
//   users/{uid}/sessions/{sessionId}
// One doc per session. Read by the admin dashboard for engagement scoring
// (visit count, avg session length, device breakdown). Members can read
// their own; admins can read all.

const SESSIONS_SUB = "sessions";
const HEARTBEAT_MS = 60_000;
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
// lastSeenAt advances only while the member did something in the last five
// minutes. The engine cockpit reads a session's active time as
// lastSeenAt − startedAt, so a laptop left open on the page must not count
// 29 idle minutes as engagement (the session still ENDS at 30 idle minutes).
const ACTIVE_WINDOW_MS = 5 * 60 * 1000;

let activeSessionId = null;
let activeUid = null;
let heartbeatTimer = null;
let lastActivityAt = 0;

function makeSessionId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function detectDevice() {
  const ua = navigator.userAgent || "";
  const w = window.innerWidth || 0;
  if (/iPad|Tablet|PlayBook/i.test(ua) || (w >= 600 && w <= 1024 && /Mobi/i.test(ua))) return "tablet";
  if (/Mobi|Android|iPhone|iPod/i.test(ua) || w < 600) return "phone";
  return "laptop";
}

function sessionRef(uid, sessionId) {
  return doc(db, "users", uid, SESSIONS_SUB, sessionId);
}

async function start(uid) {
  if (!uid) return;
  // If a session is already active for a different user (e.g., user
  // switched accounts in the same tab), end the old one before starting
  // a fresh one. Without this, the old session would keep heart-beating
  // under the previous UID.
  if (activeSessionId && activeUid !== uid) await end();
  if (activeSessionId) return;
  const sessionId = makeSessionId();
  activeSessionId = sessionId;
  activeUid = uid;
  lastActivityAt = Date.now();

  let tz = null;
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { tz = null; }
  await setDoc(sessionRef(uid, sessionId), {
    startedAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
    endedAt: null,
    tz, // member-local zone: the cockpit buckets minutes by the member's hour, not the server's
    userAgent: navigator.userAgent || null,
    viewport: { w: window.innerWidth, h: window.innerHeight },
    deviceType: detectDevice(),
    referrer: document.referrer || null,
  }).catch(() => {});

  heartbeatTimer = window.setInterval(heartbeat, HEARTBEAT_MS);
  window.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("beforeunload", onUnload);
  ["click", "keydown", "scroll", "mousemove"].forEach((evt) =>
    window.addEventListener(evt, markActive, { passive: true })
  );
}

function markActive() {
  lastActivityAt = Date.now();
}

async function heartbeat() {
  if (!activeSessionId || !activeUid) return;
  const idleMs = Date.now() - lastActivityAt;
  if (idleMs > IDLE_TIMEOUT_MS) {
    await end();
    return;
  }
  if (idleMs > ACTIVE_WINDOW_MS) return; // visible but idle: the session lives, the clock does not
  await setDoc(
    sessionRef(activeUid, activeSessionId),
    { lastSeenAt: serverTimestamp() },
    { merge: true }
  ).catch(() => {});
}

async function end() {
  if (!activeSessionId || !activeUid) return;
  const uid = activeUid;
  const sessionId = activeSessionId;
  activeSessionId = null;
  activeUid = null;
  if (heartbeatTimer) {
    window.clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  window.removeEventListener("visibilitychange", onVisibility);
  window.removeEventListener("beforeunload", onUnload);
  ["click", "keydown", "scroll", "mousemove"].forEach((evt) =>
    window.removeEventListener(evt, markActive)
  );
  await setDoc(
    sessionRef(uid, sessionId),
    { endedAt: serverTimestamp() },
    { merge: true }
  ).catch(() => {});
}

function onVisibility() {
  if (document.visibilityState === "hidden") end();
}

function onUnload() {
  end();
}

export default {
  start,
  end,
};
